import apiV2 from '../api/v2';

export interface DashboardPractice {
    id: string;
    name: string;
    doctorFirstName: string | null;
    doctorLastName: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
    phone: string | null;
    version: string | null;
}

/**
 * The caller's own practices, served by the v2 GET /practices/mine endpoint
 * (scoped server-side to the logged-in user's practices). Returns the full set.
 */
export async function fetchMyPractices(): Promise<DashboardPractice[]> {
    const response = await apiV2.get<DashboardPractice[]>('/practices/mine');
    return response.data;
}

export interface UpdatePracticeProfileData {
    practiceId: string;
    name?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
}

interface UpdatePracticeProfileBody {
    name?: string;
    contact?: { phone?: string };
    address?: { address?: string; city?: string; state?: string; zip?: string };
}

/**
 * Builds the nested v2 body from the flat form fields, omitting `contact`/`address`
 * blocks entirely when none of their fields are set — an empty block is a 400 server-side.
 */
function buildProfileBody(data: UpdatePracticeProfileData): UpdatePracticeProfileBody {
    const body: UpdatePracticeProfileBody = {};
    if (data.name) body.name = data.name;

    const contact: NonNullable<UpdatePracticeProfileBody['contact']> = {};
    if (data.phone) contact.phone = data.phone;
    if (Object.keys(contact).length > 0) body.contact = contact;

    const address: NonNullable<UpdatePracticeProfileBody['address']> = {};
    if (data.address) address.address = data.address;
    if (data.city) address.city = data.city;
    if (data.state) address.state = data.state;
    if (data.zip) address.zip = data.zip;
    if (Object.keys(address).length > 0) body.address = address;

    return body;
}

// Returns void deliberately: the endpoint responds with the nested practice-detail
// shape, not the flat DashboardPractice used elsewhere here. Callers reload via
// fetchMyPractices rather than consuming the response.
export async function updatePracticeProfile(data: UpdatePracticeProfileData): Promise<void> {
    await apiV2.patch(`/practices/${data.practiceId}/profile`, buildProfileBody(data));
}

export interface AdminPractice {
    id: string;
    name: string;
    doctorName: string | null;
}

const PRACTICES_PAGE_SIZE = 100;

/**
 * All practices in the system, for the admin practices table. Served by the
 * admin v2 GET /practices endpoint, which is paginated (limit ≤ 100); we page
 * through until a short batch signals the end. apiV2 unwraps the envelope, so
 * pagination meta isn't available — a short page is the terminating condition.
 */
export async function fetchAllPractices(): Promise<AdminPractice[]> {
    const all: AdminPractice[] = [];
    let page = 1;
    for (;;) {
        const response = await apiV2.get<AdminPractice[]>('/practices', {
            params: { page, limit: PRACTICES_PAGE_SIZE }
        });
        const batch = response.data;
        all.push(...batch);
        if (batch.length < PRACTICES_PAGE_SIZE) break;
        page += 1;
    }
    return all;
}
